import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import * as Sentry from '@sentry/react';
import { config } from '../core/config';

class InsightsService {
  private appInsights: ApplicationInsights | null = null;

  initialize() {
    // Application Insights is not configured for this project
    // This service is kept as a placeholder for future implementation
    console.log('Application Insights not configured');
    return;
  }

  initializeWithKey(instrumentationKey: string) {
    if (!instrumentationKey) {
      console.log('Application Insights key not provided');
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: instrumentationKey,
          enableAutoRouteTracking: true,
          disableFetchTracking: false,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          correlationHeaderExcludedDomains: ['*.blob.core.windows.net'],
          disableExceptionTracking: false,
          autoTrackPageVisitTime: true,
          disableAjaxTracking: false,
          appId: `shzoom-uptime-web-${config.environment}`,
          namePrefix: 'shzoom-uptime-web'
        }
      });

      this.appInsights.loadAppInsights();
      
      // Set cloud role for better filtering
      this.appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.tags) {
          envelope.tags['ai.cloud.role'] = 'shzoom-uptime-web';
          envelope.tags['ai.cloud.roleInstance'] = config.environment;
        }
      });

      // Track initial page view
      this.appInsights.trackPageView({
        name: 'App Started',
        properties: {
          environment: config.environment,
          version: config.appVersion
        }
      });

    } catch {
      // App Insights init failure is captured by the global error handler
    }
  }

  trackEvent(name: string, properties?: { [key: string]: string | number | boolean }) {
    if (this.appInsights) {
      this.appInsights.trackEvent({ name }, properties);
    }
  }

  trackException(error: Error, properties?: { [key: string]: string | number | boolean }) {
    if (this.appInsights) {
      this.appInsights.trackException({ exception: error, properties });
    }
    Sentry.captureException(error, { data: properties });
  }

  trackPageView(name: string, url?: string, properties?: { [key: string]: string | number | boolean }) {
    if (this.appInsights) {
      this.appInsights.trackPageView({ name, uri: url, properties });
    }
  }

  trackMetric(name: string, average: number, properties?: { [key: string]: string | number | boolean }) {
    if (this.appInsights) {
      this.appInsights.trackMetric({ name, average }, properties);
    }
  }

  setAuthenticatedUserContext(authenticatedUserId: string) {
    if (this.appInsights) {
      this.appInsights.setAuthenticatedUserContext(authenticatedUserId);
    }
    Sentry.setUser({ id: authenticatedUserId });
  }

  clearAuthenticatedUserContext() {
    if (this.appInsights) {
      this.appInsights.clearAuthenticatedUserContext();
    }
    Sentry.setUser(null);
  }

  flush() {
    if (this.appInsights) {
      this.appInsights.flush();
    }
  }
}

export const Insights = new InsightsService();
export default Insights;